// src/components/onboarding/onboardingQuotes.js
import { makeQuoteRecord, pipelineQuoteTitle } from "../quotes/quoteRecord";
/* ⚠ The quantity rule lives with the block's own money maths — one definition,
   imported rather than restated, because THIS function is the boundary the two
   sides used to disagree across. See quantityOf's comment. */
import { quantityOf } from "../proposals/foundationalBlocks";
//
// Stage D of the onboarding pipeline: one Quote per foundational block.
//
// PURE and React-free, so the mapping can be unit-tested without rendering the
// app — the same reason opportunityPipeline.js and webViewBadges.js are.
//
// ── WHY THIS IS FRONTEND, NOT A BACKEND STAGE ──────────────────────────────
// Quotes live in the per-user app-state blob, which saveUserState overwrites
// WHOLE with no ETag on the SPA's debounced PUT. A backend route appending a
// quote would be a lost update against the SPA exactly the way the proposal
// view's tracking data would have been — so Stage D is built where the state
// actually lives, and goes through the same commit()/saveSnapshot path every
// other quote write uses. Stages A/B/C write to Autotask and stay on the
// backend.
//
// ⚠ Shapes are copied from the REAL factories, not from the framework's summary
// of them: convertBacklogToQuote (MspOsApp.jsx:3643), addSection (:3873),
// addOption (:3943) and makeNewLineItem (:3998). The summary omitted `type` and
// `supplierSku`, and a quote missing fields the builder expects breaks on open,
// not on create.

/* Foundational line items carry three fields the Quoting line-item factory has
   no home for — `sku`, `manufacturer` and `stockNote` are AI-suggestion-only
   there — plus `detail`, which is prose the customer actually wrote.

   Dropping `detail` silently would lose real content, so it is folded into the
   description rather than discarded. Everything else maps straight across:
   `supplier`, `supplierSku` and `imageUrl` DO exist on the real factory, which
   the framework's field list missed. */
function toQuoteLineItem(li, idx, seed) {
  const description = [li.description, li.detail]
    .map((s) => String(s || "").trim())
    .filter(Boolean)
    .join(" — ");

  return {
    id: `li-${seed}-${idx}`,
    type: "hardware",
    description,
    /* ⛔ NOT `Number(li.quantity) || 0`. This line does not just display a
       quantity, it FREEZES one into a new stored quote — so a block line with an
       absent quantity was written as a permanent 0 and the customer was quoted
       nothing for an item the proposal had priced. */
    quantity: quantityOf(li),
    unitCost: Number(li.unitCost) || 0,
    unitPrice: Number(li.unitPrice) || 0,
    // Stage 4 customer-side fields — blanks, per the factory.
    customerCanEditQty: false,
    customerCanToggle: false,
    // These four DO exist on both shapes.
    supplierId: "",
    supplier: li.supplier || "",
    supplierSku: li.supplierSku || "",
    supplierUrl: "",
    supplierNotes: "",
    // null lets the backend freight calculator resolve a weight.
    weightKg: null,
    weightSource: null,
    imageUrl: li.imageUrl || "",
  };
}

/* The shell every Stage D quote shares. Extracted so the Setup Fees quote and
   the per-block quotes cannot drift apart — a field the builder expects on one
   and not the other breaks on OPEN, not on create, which is the hardest kind of
   difference to notice. */
async function quoteShell(proposal, { seed, now, title, description, sectionLabel, lineItems, reserve }) {
  const content = proposal.content || {};
  const sectionId = `sec-${seed}`;
  const optionId = `opt-${seed}`;
  return makeQuoteRecord({
    seed, now, reserve,
    // Stage D has no backlog item. The builder must tolerate null here.
    backlogId: null,
    // A proposal carries no Autotask company (the dropdown was removed), so
    // these stay blank rather than being guessed at. The contact IS known.
    companyId: proposal.companyId || "",
    companyName: proposal.companyName || content.customerName || "",
    contactName: content.customerContact || content.customerFullName || "",
    contactEmail: content.customerEmail || "",
    title,
    description,
    sections: [{
      id: sectionId,
      label: sectionLabel,
      description,
      // addOption makes the first option in a section the selection; a section
      // with options but no selectedOptionId renders as nothing chosen, so it
      // is set here rather than left to the builder.
      selectedOptionId: optionId,
      createdByAI: false,
      options: [{ id: optionId, label: "Standard", description: "", lineItems }],
    }],
    /* commonLineItems, status, validUntil, sentAt, decidedAt, notes, pdfUrl
       and autotaskQuoteId all come from makeQuoteRecord now. Repeating them
       here would be dead weight the factory silently ignores — and the day one
       of them changed, this copy would be the one left behind. */

    /* THE POINTER STAGE D EXISTS TO SET. Nothing else in the system writes
       proposalId automatically — it is otherwise only ever set by hand from the
       builder's "Related proposal" dropdown — so a quote created here without it
       is orphaned from the deal that produced it. */
    proposalId: proposal.id,
  });
}

/* ── Setup fees ─────────────────────────────────────────────────────────────
   Three separate charges on the proposal, quoted as one document.

   ⚠ THE WAIVER DOES NOT ZERO THE FIELDS. `waiveSetupFees` decides whether the
   two FIXED fees are charged; the amounts stay at their real values (625/599)
   so unwaiving restores them. Reading the raw fields would bill a client for
   fees their proposal explicitly waived — and three of the ten live proposals
   have the waiver on.

   The per-device Intune enrolment is NOT covered by the waiver. That is the
   templates' own rule, not an assumption: "Waiver covers the two fixed setup
   fees only — the per-device Intune enrolment line is separate work and is
   still charged" (BronzeTemplate.jsx:300). It is also usually the largest of
   the three — 5 devices x $110 is $550, more than either fixed fee. */
export function setupFeeLines(content = {}) {
  const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const waived = !!content.waiveSetupFees;
  const deviceCount = num(content.deviceCount);

  return [
    {
      key: "onboardingFee",
      description: "Onboarding fee",
      quantity: 1,
      unitPrice: waived ? 0 : num(content.onboardingFee),
      waived,
    },
    {
      key: "intuneSetupFee",
      description: "Intune migration & setup",
      quantity: 1,
      unitPrice: waived ? 0 : num(content.intuneSetupFee),
      waived,
    },
    {
      key: "intunePerDevRate",
      description: "Intune device enrolment & configuration",
      // Per DEVICE — the quantity carries the count, so the line reads
      // "8 x $110" on the quote rather than hiding the maths in one number.
      quantity: deviceCount,
      unitPrice: num(content.intunePerDevRate),
      waived: false,
    },
  ];
}

/** The fee lines that would actually be charged — anything that bills nothing is dropped. */
export function chargeableSetupFeeLines(content = {}) {
  return setupFeeLines(content).filter((l) => l.unitPrice > 0 && l.quantity > 0);
}

/**
 * The Setup Fees quote, or null when nothing would be charged.
 *
 * Returns null rather than an empty quote: a quote with no line items is a
 * document someone has to open, read and delete to discover it says nothing.
 */
export async function buildSetupFeesQuote(proposal, { now = Date.now(), reserve } = {}) {
  if (!proposal) return null;
  const content = proposal.content || {};
  const lines = chargeableSetupFeeLines(content);
  if (!lines.length) return null;

  const seed = `${now}-fees`;
  const clientName = proposal.companyName || content.customerName || "";
  const q = await quoteShell(proposal, {
    seed, now, reserve,
    /* Carries the SOURCE PROPOSAL's P number, not this quote's own Q — see
       pipelineQuoteTitle. A standalone quote's title is never rewritten. */
    title: pipelineQuoteTitle(proposal, "Setup Fees", clientName),
    description: "One-off setup and onboarding charges.",
    sectionLabel: "Setup Fees",
    lineItems: lines.map((l, j) => ({
      id: `li-${seed}-${j}`,
      type: "service",          // not hardware — nothing is being shipped
      description: l.description,
      quantity: l.quantity,
      // No internal cost is known for these, and inventing one would put a
      // fictional margin on the quote.
      unitCost: 0,
      unitPrice: l.unitPrice,
      customerCanEditQty: false,
      customerCanToggle: false,
      supplierId: "", supplier: "", supplierSku: "", supplierUrl: "", supplierNotes: "",
      weightKg: null, weightSource: null,
      imageUrl: "",
    })),
  });
  // Its own kind, NOT a block title — a foundational block legitimately called
  // "Setup Fees" must not suppress this quote or be suppressed by it.
  q.onboardingKind = "setup-fees";
  return q;
}

/**
 * Build the quotes Stage D would add. Returns an array; writes nothing.
 *
 * @param proposal  the proposal record
 * @param now       injectable clock, so ids are deterministic in tests
 */
export async function buildOnboardingQuotes(proposal, { now = Date.now(), reserve } = {}) {
  if (!proposal) return [];
  const content = proposal.content || {};
  const blocks = content.foundationalBlocks || [];
  const clientName = proposal.companyName || content.customerName || "";

  /* ⚠ Promise.all, so N quotes reserve N numbers CONCURRENTLY. This is the
     contended case the server counter was built and proven for — it is
     compare-and-swap with a bounded retry, verified against live Blob Storage
     with eight simultaneous callers getting eight distinct numbers. */
  const blockQuotes = await Promise.all(blocks.map(async (block, i) => {
    /* ⚠ `qt-${Date.now()}` is what convertBacklogToQuote uses, and it is only
       safe because it creates ONE quote at a time. Stage D creates five in the
       same millisecond, so the index is part of the id — without it every quote
       after the first collides, and React keys and every id lookup in the
       builder silently address the wrong record. */
    const seed = `${now}-${i}`;
    const q = await quoteShell(proposal, {
      seed, now, reserve,
      title: pipelineQuoteTitle(proposal, block.title || "Untitled block", clientName),
      description: block.description || "",
      sectionLabel: block.title || "Scope",
      lineItems: (block.lineItems || []).map((li, j) => toQuoteLineItem(li, j, seed)),
    });
    // Stamped so a re-run can tell its own output from a hand-made quote.
    q.onboardingKind = "block";
    /* ⚠ STILL THE RAW BLOCK TITLE, never the rendered one. This is the key
       existingOnboardingQuotes dedupes on, so tying it to the display format
       would make every title change re-create every quote — the same trap the
       ticket matchKey exists to avoid. */
    q.onboardingBlockTitle = block.title || "";
    return q;
  }));

  // The fees quote comes FIRST: it is the one a client is asked to pay before
  // any project work starts.
  const fees = await buildSetupFeesQuote(proposal, { now, reserve });
  return fees ? [fees, ...blockQuotes] : blockQuotes;
}

/**
 * The Stage D quotes that already exist for this proposal.
 *
 * Matched on `proposalId` plus the stamp, never on quote id: a re-run mints new
 * ids, so comparing ids would create a full duplicate set every single time.
 */
export function existingOnboardingQuotes(quotes = [], proposalId) {
  if (!proposalId) return [];        // an undefined id must not match undefined
  return quotes.filter((q) =>
    q && q.proposalId === proposalId && (q.onboardingKind || q.onboardingBlockTitle));
}

/* The identity of one Stage D quote. The fees quote is keyed by KIND, block
   quotes by their title — so a foundational block someone names "Setup Fees"
   neither suppresses the real fees quote nor is suppressed by it.

   Quotes written before onboardingKind existed carry only a block title, and
   still resolve correctly. */
function quoteKey(q) {
  return q.onboardingKind === "setup-fees" ? "kind:setup-fees" : `block:${q.onboardingBlockTitle || ""}`;
}

/**
 * The quotes still missing for this proposal. Re-running Stage D adds only what
 * is absent, so a partial failure can be retried without duplicating what
 * already succeeded.
 */
export async function missingOnboardingQuotes(proposal, quotes = [], opts = {}) {
  const already = new Set(existingOnboardingQuotes(quotes, proposal?.id).map(quoteKey));
  /* ⚠ EVERY candidate reserves a Q number, including the ones filtered out
     below. Those numbers are then never used, leaving gaps in the sequence.
     That is the correct trade and the same one the proposal counter makes: a
     gap is a curiosity, a reused number is a defect. Building the records
     first is also what keeps the dedupe reading the same shape it always has. */
  const built = await buildOnboardingQuotes(proposal, opts);
  return built.filter((q) => !already.has(quoteKey(q)));
}
